import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Stethoscope } from "lucide-react";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-paper-50/80 border-b border-paper-200" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-[72px]">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <span className="flex items-center justify-center w-9 h-9 rounded-sm bg-forest text-paper-50 group-hover:bg-forest-700 transition-colors">
            <Stethoscope className="w-5 h-5" strokeWidth={2.2} />
          </span>
          <span className="font-display font-black text-lg tracking-tight text-ink leading-none">
            The STEM Vet<span className="text-terracotta">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium link-underline transition-colors ${
                  isActive ? "text-forest" : "text-ink hover:text-forest"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <button
            onClick={() => navigate("/contact")}
            data-testid="nav-cta-book"
            className="inline-flex items-center gap-2 bg-forest text-paper-50 px-5 py-2.5 text-sm font-semibold rounded-sm hover:bg-forest-700 hover:-translate-y-0.5 transition-all"
          >
            Book a session
          </button>
        </div>

        <button
          className="md:hidden text-ink p-2 -mr-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-paper-50 border-t border-paper-200 px-5 py-4" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-base font-medium rounded-sm ${
                    isActive ? "bg-forest text-paper-50" : "text-ink hover:bg-paper-100"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                navigate("/contact");
              }}
              data-testid="mobile-cta-book"
              className="mt-3 inline-flex items-center justify-center bg-terracotta text-paper-50 px-5 py-3 text-sm font-semibold rounded-sm hover:bg-terracotta-dark transition-colors"
            >
              Book a session
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
